import type { Breakpoint } from '@ctw/config/zui';
import { Alert } from '@ctw/shared/components/alert';
import { ContentError } from '@ctw/shared/components/errors/content-error';
import { HorizontalDivider } from '@ctw/shared/components/horizontal-divider';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import {
  type TableOfContentsItem,
  TableOfContentsScrollSpy,
} from '@ctw/shared/components/navigation/table-of-contents-scroll-spy';
import { classifyCdaDocumentError } from '@ctw/shared/content/document-viewer/_internal/renderers/cda-document-error';
import { useCdaSectionSelectionContext } from '@ctw/shared/content/document-viewer/_internal/renderers/context/cda-section-selection-provider';
import { ReactSectionRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/react/react-section-renderer';
import type { DocumentViewerControls } from '@ctw/shared/content/document-viewer/document-viewer';
import { SupportFormLink } from '@ctw/shared/content/support-form-link';
import { useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import { type ScrollSpy, useScrollSpy } from '@ctw/shared/hooks/use-scroll-spy';
import {
  DOCUMENT_RENDERER_CONTENT_TEST_ID,
  DOCUMENT_VIEWER_SECTION_CLASSNAME,
} from '@ctw/shared/test/ids';
import type { CdaDocument } from '@ctw/shared/utils/cda/types';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import type { NonEmptyArray } from '@ctw/shared/utils/types';
import { Fragment, memo, type RefObject, useMemo, useRef } from 'react';

interface ReactCdaRenderProps {
  controls: DocumentViewerControls;
  cdaDocument: CdaDocument;
  contentRef: RefObject<HTMLDivElement | null>;
}

// The article's flex direction and the table of contents' collapse-to-tabs point are two
// independent reads of the same container width — sourcing both from this single constant is
// what keeps them from ever disagreeing about which layout the current width calls for.
const TABLE_OF_CONTENTS_COLLAPSE_BREAKPOINT: Breakpoint = 'md';

export const ReactCdaRenderer = memo(
  ({ controls, cdaDocument, contentRef }: ReactCdaRenderProps) => {
    const containerRef = useRef<HTMLElement>(null);
    const { breakpoints, width: containerWidth } = useContainerQuery({ containerRef });
    const documentScrollSpy = useScrollSpy({ debounceMilliseconds: 20 });

    if ((cdaDocument.content?.sections ?? []).length === 0) {
      const errorKind = classifyCdaDocumentError(cdaDocument);

      if (errorKind === 'no-content') {
        return (
          <ContentError
            title="There is no content available to display for this document."
            className={tw`flex h-full max-h-full min-h-full w-full items-center justify-center`}
          >
            <span>If you believe this is an issue, please </span>
            <SupportFormLink buttonText="contact support" />.
          </ContentError>
        );
      }

      if (errorKind === 'fetch-failure') {
        return (
          <ContentError
            title="Unable to load this document"
            className={tw`flex h-full max-h-full min-h-full w-full items-center justify-center`}
          >
            <span>If this problem persists </span>
            <SupportFormLink buttonText="contact support" />.
          </ContentError>
        );
      }

      if (errorKind === 'too-large') {
        return (
          <ContentError
            title="This document is too large to display"
            className={tw`flex h-full max-h-full min-h-full w-full items-center justify-center`}
          >
            <span>
              Documents over 20 MB cannot be displayed in the viewer. Download the original document
              to view it instead. If you believe this is an issue, please{' '}
            </span>
            <SupportFormLink buttonText="contact support" />.
          </ContentError>
        );
      }

      return (
        <ContentError
          title="This document cannot be shown due to invalid XML. Download the XML to view the document."
          className={tw`flex h-full max-h-full min-h-full w-full items-center justify-center`}
        >
          <span>If you believe this is an issue, please </span>
          <SupportFormLink buttonText="contact support" />.
        </ContentError>
      );
    }

    return (
      <article
        ref={containerRef}
        id={cdaDocument.id}
        className={twx('relative flex w-full max-w-full flex-col-reverse gap-x-4 overflow-hidden', {
          'flex-row': !breakpoints.isAtMost[TABLE_OF_CONTENTS_COLLAPSE_BREAKPOINT],
        })}
      >
        <div
          ref={contentRef}
          data-testid={DOCUMENT_RENDERER_CONTENT_TEST_ID}
          className={tw`cda-renderer-content no-scrollbar scroll-shadows flex flex-1 flex-col gap-[12px] overflow-y-auto overflow-x-hidden p-5`}
          onScroll={documentScrollSpy.onScroll}
        >
          {cdaDocument.documentIsExcessivelyLarge && (
            <Alert type="caution">
              This document is over 5MB in size. This may cause performance issues while viewing the
              document. If you experience issues viewing the document, you can try closing unused
              browser tabs and applications, or downloading the document instead.
            </Alert>
          )}
          {(classifyCdaDocumentError(cdaDocument) === 'parse-error' ||
            cdaDocument.content?.sections.some((section) => section.parseError !== undefined)) && (
            <Alert type="caution">
              This document contains sections which could not be shown due to invalid XML. Download
              the XML to view in full.
            </Alert>
          )}
          <CdaDocumentSections cdaDocument={cdaDocument} />
        </div>
        <CdaDocumentTableOfContents
          cdaDocument={cdaDocument}
          controls={controls}
          scrollSpy={documentScrollSpy.scrollSpy}
          containerRef={containerRef}
          contentRef={contentRef}
        />
        <div
          className={twx('absolute top-0 right-0 bottom-0 left-0 h-full w-full bg-white', {
            hidden: containerWidth > 0,
          })}
        >
          <LoadingSpinner
            message="Previewing document..."
            centered={true}
            className={tw`min-h-[20vh]`}
          />
        </div>
      </article>
    );
  },
  (prevProps, nextProps) => prevProps.cdaDocument.id === nextProps.cdaDocument.id,
);

interface CdaDocumentSectionsProps {
  cdaDocument: CdaDocument;
}

const CdaDocumentSections = memo(
  ({ cdaDocument }: CdaDocumentSectionsProps) => {
    return (cdaDocument.content?.sections ?? []).map((section, index) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: stable list, items are not reordered
      <Fragment key={`section-${section.id}-${index}`}>
        {index !== 0 && <HorizontalDivider className={tw`mt-5 mb-0`} />}
        <ReactSectionRenderer cdaSection={section} />
      </Fragment>
    ));
  },
  (prevProps, nextProps) => prevProps.cdaDocument.id === nextProps.cdaDocument.id,
);

interface CdaDocumentTableOfContentsProps {
  cdaDocument: CdaDocument;
  controls: DocumentViewerControls;
  scrollSpy: ScrollSpy;
  containerRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
}

const CdaDocumentTableOfContents = ({
  cdaDocument,
  controls,
  scrollSpy,
  containerRef,
  contentRef,
}: CdaDocumentTableOfContentsProps) => {
  const { selectedSectionIds } = useCdaSectionSelectionContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally over-specific deps
  const tableOfContentsItems: Array<TableOfContentsItem> = useMemo(
    () =>
      cdaDocument.content?.sections.map((section) => ({
        title: section.title,
        urlParameterId: section.id,
        contentDomId: section.domId,
        selected: selectedSectionIds.includes(section.id),
      })) ?? [],
    [cdaDocument.id, selectedSectionIds],
  );

  if (tableOfContentsItems.length === 0) {
    return undefined;
  }

  return (
    <TableOfContentsScrollSpy
      key={cdaDocument.id}
      title="Document Table of Contents"
      items={tableOfContentsItems as NonEmptyArray<(typeof tableOfContentsItems)[0]>}
      scrollSpy={scrollSpy}
      contentRef={contentRef}
      containerRef={containerRef}
      urlParameterName="documentSectionId"
      contentDomClass={DOCUMENT_VIEWER_SECTION_CLASSNAME}
      deepLinkItems={controls.deepLinkSections}
      contentBreakpoints={{ collapseToTabsAtOrBelow: TABLE_OF_CONTENTS_COLLAPSE_BREAKPOINT }}
    />
  );
};
